import {
  Assignment,
  AuditLogEvent,
  CAStaffMember,
  CADashboardAnalytics,
  CAClientDetail,
  CAClientListItem,
  CAClientMapping,
  ClientOrganization,
  ComplianceStatus,
  DocumentRequest,
  DocumentFolderNode,
  DocumentWorkflowRequirement,
  Engagement,
  DocumentRequestItem,
  Task,
} from '../contracts/ca.types';

type AnyRecord = Record<string, unknown>;

const asRecord = (value: unknown): AnyRecord => (value && typeof value === 'object' ? (value as AnyRecord) : {});

const pickString = (obj: AnyRecord, keys: string[], fallback = ''): string => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'string' && value.trim().length > 0) return value;
  }
  return fallback;
};

const pickBoolean = (obj: AnyRecord, keys: string[], fallback = false): boolean => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'boolean') return value;
  }
  return fallback;
};

const pickNumber = (obj: AnyRecord, keys: string[], fallback = 0): number => {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return fallback;
};

const pickArray = (obj: AnyRecord, keys: string[]): unknown[] => {
  for (const key of keys) {
    const value = obj[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const normalizeOrganizationId = (obj: AnyRecord): string =>
  pickString(obj, ['organizationId', 'organization_id', 'tenantId', 'tenant_id', 'companyId', 'company_id']);

const normalizeClientOrganizationId = (obj: AnyRecord): string =>
  pickString(obj, ['clientOrganizationId', 'client_organization_id', 'clientOrgId', 'client_org_id', 'organizationId', 'organization_id']);

const normalizeMappingId = (obj: AnyRecord): string =>
  pickString(obj, ['mappingId', 'mapping_id', 'id']);

const normalizeEngagement = (payload: unknown): Engagement => {
  const src = asRecord(payload);
  const pitchNotes = pickString(src, ['pitchNotes', 'pitch_notes']);
  const pitchDate = pickString(src, ['pitchDate', 'pitch_date']);
  const inviteStatus = pickString(src, ['inviteStatus', 'invite_status']);
  const inviteId = pickString(src, ['inviteId', 'invite_id']);
  const inviteSentAt = pickString(src, ['inviteSentAt', 'invite_sent_at']);
  const acceptedAt = pickString(src, ['acceptedAt', 'accepted_at']);
  return {
    id: pickString(src, ['id']),
    firmId: pickString(src, ['firmId', 'firm_id']),
    clientOrganizationId: normalizeClientOrganizationId(src),
    stage: (pickString(src, ['stage']) || 'lead') as Engagement['stage'],
    segment: (pickString(src, ['segment']) || 'new') as Engagement['segment'],
    ...(pitchNotes ? { pitchNotes } : {}),
    ...(pitchDate ? { pitchDate } : {}),
    ...(inviteStatus ? { inviteStatus: inviteStatus as Engagement['inviteStatus'] } : {}),
    ...(inviteId ? { inviteId } : {}),
    ...(inviteSentAt ? { inviteSentAt } : {}),
    ...(acceptedAt ? { acceptedAt } : {}),
    billingReadiness: (pickString(src, ['billingReadiness', 'billing_readiness']) || 'pending') as Engagement['billingReadiness'],
    createdAt: pickString(src, ['createdAt', 'created_at']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

const normalizeClientOrganization = (payload: unknown): ClientOrganization => {
  const src = asRecord(payload);
  const gstin = pickString(src, ['gstin']);
  const pan = pickString(src, ['pan']);
  const industry = pickString(src, ['industry']);
  return {
    id: pickString(src, ['id', 'organizationId', 'organization_id']),
    organizationId: normalizeOrganizationId(src),
    legalName: pickString(src, ['legalName', 'legal_name']),
    displayName: pickString(src, ['displayName', 'display_name']),
    ...(gstin ? { gstin } : {}),
    ...(pan ? { pan } : {}),
    ...(industry ? { industry } : {}),
    status: (pickString(src, ['status']) || 'active') as ClientOrganization['status'],
    createdAt: pickString(src, ['createdAt', 'created_at']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

const normalizeCompliance = (payload: unknown): ComplianceStatus => {
  const src = asRecord(payload);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  return {
    id: pickString(src, ['id']),
    organizationId: normalizeOrganizationId(src),
    firmId: pickString(src, ['firmId', 'firm_id']),
    clientOrganizationId: normalizeClientOrganizationId(src),
    periodKey: pickString(src, ['periodKey', 'period_key']),
    status: (pickString(src, ['status']) || 'on_track') as ComplianceStatus['status'],
    ...(dueDate ? { dueDate } : {}),
    pendingCount: pickNumber(src, ['pendingCount', 'pending_count']),
    blockerCount: pickNumber(src, ['blockerCount', 'blocker_count']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeMapping = (payload: unknown): CAClientMapping => {
  const src = asRecord(payload);
  const revokedAt = pickString(src, ['revokedAt', 'revoked_at']);
  return {
    id: pickString(src, ['id', 'mappingId', 'mapping_id']),
    organizationId: normalizeOrganizationId(src),
    firmId: pickString(src, ['firmId', 'firm_id']),
    clientOrganizationId: normalizeClientOrganizationId(src),
    accessLevel: (pickString(src, ['accessLevel', 'access_level']) || 'review') as CAClientMapping['accessLevel'],
    status: (pickString(src, ['status']) || 'active') as CAClientMapping['status'],
    effectiveFrom: pickString(src, ['effectiveFrom', 'effective_from']),
    ...(revokedAt ? { revokedAt } : {}),
    createdByUserId: pickString(src, ['createdByUserId', 'created_by_user_id']),
    createdAt: pickString(src, ['createdAt', 'created_at']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

const normalizeDocumentRequestItem = (payload: unknown): DocumentRequestItem => {
  const src = asRecord(payload);
  return {
    id: pickString(src, ['id']),
    key: pickString(src, ['key']),
    label: pickString(src, ['label']),
    required: pickBoolean(src, ['required']),
    accepted: pickBoolean(src, ['accepted']),
  };
};

export const normalizeTask = (payload: unknown): Task => {
  const src = asRecord(payload);
  const description = pickString(src, ['description']);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  const assigneeUserId = pickString(src, ['assigneeUserId', 'assignee_user_id']);
  return {
    id: pickString(src, ['id']),
    organizationId: normalizeOrganizationId(src),
    firmId: pickString(src, ['firmId', 'firm_id']),
    mappingId: normalizeMappingId(src),
    clientOrganizationId: normalizeClientOrganizationId(src),
    title: pickString(src, ['title']),
    ...(description ? { description } : {}),
    status: (pickString(src, ['status']) || 'todo') as Task['status'],
    priority: (pickString(src, ['priority']) || 'medium') as Task['priority'],
    ...(dueDate ? { dueDate } : {}),
    ...(assigneeUserId ? { assigneeUserId } : {}),
    ownerSide: (pickString(src, ['ownerSide', 'owner_side']) || 'ca') as Task['ownerSide'],
    createdByUserId: pickString(src, ['createdByUserId', 'created_by_user_id']),
    createdAt: pickString(src, ['createdAt', 'created_at']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeDocumentRequest = (payload: unknown): DocumentRequest => {
  const src = asRecord(payload);
  const itemPayloads = pickArray(src, ['items']).map(normalizeDocumentRequestItem);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  return {
    id: pickString(src, ['id']),
    organizationId: normalizeOrganizationId(src),
    firmId: pickString(src, ['firmId', 'firm_id']),
    mappingId: normalizeMappingId(src),
    clientOrganizationId: normalizeClientOrganizationId(src),
    title: pickString(src, ['title']),
    status: (pickString(src, ['status']) || 'requested') as DocumentRequest['status'],
    ...(dueDate ? { dueDate } : {}),
    items: itemPayloads,
    createdByUserId: pickString(src, ['createdByUserId', 'created_by_user_id']),
    createdAt: pickString(src, ['createdAt', 'created_at']),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeCAClientListItem = (payload: unknown): CAClientListItem => {
  const src = asRecord(payload);
  const engagement = asRecord(src.engagement);
  const hasEngagement = Object.keys(engagement).length > 0;
  const clientStatus = pickString(src, ['clientStatus', 'client_status']).toUpperCase();
  const contactEmail = pickString(src, ['contactEmail', 'contact_email', 'email']);
  const contactPhone = pickString(src, ['contactPhone', 'contact_phone', 'phone']);
  return {
    mapping: normalizeMapping(src.mapping),
    organization: normalizeClientOrganization(src.organization),
    compliance: normalizeCompliance(src.compliance),
    ...(clientStatus ? { clientStatus: clientStatus as CAClientListItem['clientStatus'] } : {}),
    ...(contactEmail ? { contactEmail } : {}),
    ...(contactPhone ? { contactPhone } : {}),
    ...(hasEngagement ? { engagement: normalizeEngagement(engagement) } : {}),
  };
};

export const normalizeCAClientDetail = (payload: unknown): CAClientDetail => {
  const src = asRecord(payload);
  const summary = asRecord(src.summary);
  return {
    mapping: normalizeMapping(src.mapping),
    organization: normalizeClientOrganization(src.organization),
    compliance: normalizeCompliance(src.compliance),
    summary: {
      pendingTasks: pickNumber(summary, ['pendingTasks', 'pending_tasks']),
      overdueTasks: pickNumber(summary, ['overdueTasks', 'overdue_tasks']),
      pendingDocuments: pickNumber(summary, ['pendingDocuments', 'pending_documents']),
      openDocumentRequests: pickNumber(summary, ['openDocumentRequests', 'open_document_requests']),
    },
  };
};

export const normalizeAssignment = (payload: unknown): Assignment => {
  const src = asRecord(payload);
  const assigneeName = pickString(src, ['assigneeName', 'assignee_name']);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  return {
    id: pickString(src, ['id']),
    taskId: pickString(src, ['taskId', 'task_id']),
    mappingId: normalizeMappingId(src),
    clientOrganizationId: normalizeClientOrganizationId(src),
    assigneeUserId: pickString(src, ['assigneeUserId', 'assignee_user_id']),
    ...(assigneeName ? { assigneeName } : {}),
    ownerUserId: pickString(src, ['ownerUserId', 'owner_user_id']),
    ...(dueDate ? { dueDate } : {}),
    dependencyTaskIds: pickArray(src, ['dependencyTaskIds', 'dependency_task_ids']).map((id) => String(id)),
    slaState: (pickString(src, ['slaState', 'sla_state']) || 'on_track') as Assignment['slaState'],
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeDocumentFolderNode = (payload: unknown): DocumentFolderNode => {
  const src = asRecord(payload);
  const parentId = pickString(src, ['parentId', 'parent_id']);
  const fileStatus = pickString(src, ['fileStatus', 'file_status']);
  const docType = pickString(src, ['docType', 'doc_type']);
  const owner = pickString(src, ['owner']);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  return {
    id: pickString(src, ['id']),
    mappingId: normalizeMappingId(src),
    clientOrganizationId: normalizeClientOrganizationId(src),
    ...(parentId ? { parentId } : {}),
    name: pickString(src, ['name']),
    itemType: (pickString(src, ['itemType', 'item_type']) || 'file') as DocumentFolderNode['itemType'],
    ...(fileStatus ? { fileStatus: fileStatus as DocumentFolderNode['fileStatus'] } : {}),
    version: pickNumber(src, ['version'], 1),
    tags: pickArray(src, ['tags']).map((tag) => String(tag)),
    ...(docType ? { docType } : {}),
    ...(owner ? { owner } : {}),
    ...(dueDate ? { dueDate } : {}),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeDocumentWorkflowRequirement = (payload: unknown): DocumentWorkflowRequirement => {
  const src = asRecord(payload);
  const owner = pickString(src, ['owner']);
  const dueDate = pickString(src, ['dueDate', 'due_date']);
  const lastReminderAt = pickString(src, ['lastReminderAt', 'last_reminder_at']);
  return {
    id: pickString(src, ['id']),
    mappingId: normalizeMappingId(src),
    clientOrganizationId: normalizeClientOrganizationId(src),
    stage: (pickString(src, ['stage']) || 'lead') as DocumentWorkflowRequirement['stage'],
    docType: pickString(src, ['docType', 'doc_type']),
    title: pickString(src, ['title']),
    status: (pickString(src, ['status']) || 'requested') as DocumentWorkflowRequirement['status'],
    ...(owner ? { owner } : {}),
    ...(dueDate ? { dueDate } : {}),
    ...(lastReminderAt ? { lastReminderAt } : {}),
    updatedAt: pickString(src, ['updatedAt', 'updated_at']),
  };
};

export const normalizeAuditLogEvent = (payload: unknown): AuditLogEvent => {
  const src = asRecord(payload);
  const metadataRecord = asRecord(src.metadata);
  const metadataEntries = Object.entries(metadataRecord).reduce<Record<string, string>>((acc, [key, value]) => {
    acc[key] = String(value);
    return acc;
  }, {});
  return {
    id: pickString(src, ['id']),
    action: pickString(src, ['action']),
    actorName: pickString(src, ['actorName', 'actor_name']),
    actorRole: pickString(src, ['actorRole', 'actor_role']),
    entityType: (pickString(src, ['entityType', 'entity_type']) || 'task') as AuditLogEvent['entityType'],
    entityId: pickString(src, ['entityId', 'entity_id']),
    timestamp: pickString(src, ['timestamp']),
    ...(Object.keys(metadataEntries).length > 0 ? { metadata: metadataEntries } : {}),
  };
};

export const normalizeDashboardAnalytics = (payload: unknown): CADashboardAnalytics => {
  const src = asRecord(payload);
  return {
    conversionRate: pickNumber(src, ['conversionRate', 'conversion_rate']),
    inviteAcceptanceRate: pickNumber(src, ['inviteAcceptanceRate', 'invite_acceptance_rate']),
    overdueTasks: pickNumber(src, ['overdueTasks', 'overdue_tasks']),
    pendingVerifications: pickNumber(src, ['pendingVerifications', 'pending_verifications']),
    slaBreaches: pickNumber(src, ['slaBreaches', 'sla_breaches']),
  };
};

export const normalizeEngagementState = normalizeEngagement;

export const normalizeStaffMember = (payload: unknown): CAStaffMember => {
  const src = asRecord(payload);
  const phone = pickString(src, ['phone']);
  return {
    id: pickString(src, ['id']),
    firmId: pickString(src, ['firmId', 'firm_id']),
    fullName: pickString(src, ['fullName', 'full_name', 'name']),
    email: pickString(src, ['email']),
    ...(phone ? { phone } : {}),
    role: (pickString(src, ['role']) || 'analyst') as CAStaffMember['role'],
    createdAt: pickString(src, ['createdAt', 'created_at']),
  };
};

